<script setup lang="ts">
import { Head } from "@inertiajs/vue3"

import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"

const { title } = useNavbar({ title: "Help" })

interface HelpSection {
  icon: string
  title: string
}

const sections: HelpSection[] = [
  { icon: ICON.ai, title: "How it works" },
  { icon: ICON.home, title: "Navigation" },
  { icon: ICON.capture, title: "Capture tips" },
  { icon: ICON.search, title: "Key features" },
]
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <div class="flex flex-col gap-2">
      <h1 class="text-highlighted text-xl font-bold">Getting Started</h1>
      <p class="text-muted text-sm">
        Ninjanizr is a home inventory app built for speed. Snap a photo, let AI describe it, and
        move on.
      </p>
    </div>

    <!-- How it works -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <UIcon :name="sections[0].icon" class="text-primary size-5" />
        <h2 class="text-highlighted font-semibold">{{ sections[0].title }}</h2>
      </div>
      <div class="text-muted flex flex-col gap-2 text-sm">
        <p>
          Everything is organized into <strong class="text-highlighted">containers</strong> — a
          house holds rooms, rooms hold furniture, furniture holds boxes, and boxes hold items. You
          can nest as deep as you need.
        </p>
        <p>The recommended workflow:</p>
        <ol class="ml-1 list-inside list-decimal space-y-1">
          <li>Create your top-level containers (house, garage, etc.)</li>
          <li>Add rooms and furniture inside them</li>
          <li>
            Open <strong class="text-highlighted">Capture</strong>, pick a container, and start
            photographing items
          </li>
          <li>AI suggests a name, description, and condition — adjust if needed, then save</li>
        </ol>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <UIcon :name="sections[1].icon" class="text-primary size-5" />
        <h2 class="text-highlighted font-semibold">{{ sections[1].title }}</h2>
      </div>
      <div class="flex flex-col gap-4">
        <div class="flex items-start gap-4">
          <UIcon :name="ICON.home" class="text-muted mt-0.5 size-4 shrink-0" />
          <div>
            <p class="text-highlighted text-sm font-medium">Dashboard</p>
            <p class="text-muted text-sm">
              Stats, recent activity, and items that need attention (missing photos or
              descriptions).
            </p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <UIcon :name="ICON.item" class="text-muted mt-0.5 size-4 shrink-0" />
          <div>
            <p class="text-highlighted text-sm font-medium">Items</p>
            <p class="text-muted text-sm">
              Search and browse all your items. Tap any item for full details, editing, and photos.
            </p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <UIcon :name="ICON.container" class="text-muted mt-0.5 size-4 shrink-0" />
          <div>
            <p class="text-highlighted text-sm font-medium">Containers</p>
            <p class="text-muted text-sm">
              Browse and manage your container hierarchy. Tap into any container to see what's
              inside.
            </p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <UIcon :name="ICON.capture" class="text-muted mt-0.5 size-4 shrink-0" />
          <div>
            <p class="text-highlighted text-sm font-medium">Capture</p>
            <p class="text-muted text-sm">
              The fast-cataloging camera. Point, shoot, review the AI suggestion, save. Repeat.
            </p>
          </div>
        </div>
        <div class="flex items-start gap-4">
          <UIcon :name="ICON.menu" class="text-muted mt-0.5 size-4 shrink-0" />
          <div>
            <p class="text-highlighted text-sm font-medium">More</p>
            <p class="text-muted text-sm">
              Tags, types, people, loans, print, import/export, members, settings, and this help
              page.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Capture tips -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <UIcon :name="sections[2].icon" class="text-primary size-5" />
        <h2 class="text-highlighted font-semibold">{{ sections[2].title }}</h2>
      </div>
      <ul class="text-muted ml-1 list-inside list-disc space-y-1 text-sm">
        <li>
          Tap the <strong class="text-highlighted">capture button</strong> or press
          <UKbd value="Space" size="sm" /> to take a photo
        </li>
        <li>AI analyzes the image and suggests a name, description, and condition</li>
        <li>
          Adjust the quantity with <strong class="text-highlighted">+/−</strong>, tap
          <strong class="text-highlighted">edit</strong> to change details, or just hit
          <strong class="text-highlighted">confirm</strong> to save
        </li>
        <li>
          The <strong class="text-highlighted">context bar</strong> at the top shows which container
          you're adding items to — tap it to switch
        </li>
        <li>Tap the gallery button to pick an existing photo instead of taking a new one</li>
        <li>
          Need a new container on the fly? Tap the
          <strong class="text-highlighted">+</strong> button to create one without leaving Capture
        </li>
        <li>After saving, you can print a label for the item right from the confirmation card</li>
        <li>If you have a barcode scanner, scan a container's QR code to switch to it instantly</li>
      </ul>
    </div>

    <!-- Key features -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <UIcon :name="sections[3].icon" class="text-primary size-5" />
        <h2 class="text-highlighted font-semibold">{{ sections[3].title }}</h2>
      </div>
      <dl class="flex flex-col gap-4 text-sm">
        <div>
          <dt class="text-highlighted font-medium">Omnisearch</dt>
          <dd class="text-muted">
            Press <UKbd value="meta" size="sm" /><UKbd value="K" size="sm" /> to search across all
            items and containers from anywhere.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Containers</dt>
          <dd class="text-muted">
            Browse and manage your hierarchy. Any container can hold other containers or items. You
            can delete a container and reassign its contents.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Inventory Progress</dt>
          <dd class="text-muted">
            Track which containers have been fully cataloged and what still needs work.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Item Types</dt>
          <dd class="text-muted">
            Categories with icons and colors. AI auto-detects types when analyzing photos.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">People</dt>
          <dd class="text-muted">Track who owns what. Assign items to household members.</dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Loans</dt>
          <dd class="text-muted">
            Record items lent out or borrowed. Active loans show a badge in the menu.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Print</dt>
          <dd class="text-muted">
            Print full labels or one-up QR labels for an item. Use Asset Tags to print batches of
            unassigned QR codes, including 3-up Standard and 8-up Mini sheets. Scan a code later to
            pull up or assign the item.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Members</dt>
          <dd class="text-muted">
            Invite others to share an inventory. Each member can add, edit, and catalog items.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Import / Export</dt>
          <dd class="text-muted">
            Export your entire inventory as a ZIP file for backup, or import from a previous export.
          </dd>
        </div>
        <div>
          <dt class="text-highlighted font-medium">Dark Mode</dt>
          <dd class="text-muted">
            Switch between light, dark, or system-matched themes using the toggle in the header.
          </dd>
        </div>
      </dl>
    </div>
  </NarrowPage>
</template>
